/**
 * Hook for fetching deal module tab counts (All Deals).
 * Provides counts for the deals module landing page tabs.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/api-client';
import { useOrganizationStore } from '@/store/organization.store';

export interface DealModuleCounts {
  allDeals: number;
}

interface UseDealModuleCountsOptions {
  enabled?: boolean;
}

// Query key for deal module counts
const dealModuleCountsKey = ['deal-module-counts'] as const;

/**
 * Fetches deal module counts
 */
async function fetchDealModuleCounts(): Promise<DealModuleCounts> {
  return apiClient<DealModuleCounts>('/deals/module-counts');
}

/**
 * Hook for fetching deal module counts.
 * Returns count for "All Deals".
 */
export function useDealModuleCounts({ enabled = true }: UseDealModuleCountsOptions = {}) {
  const queryClient = useQueryClient();
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization);

  const { data, isLoading, error } = useQuery({
    queryKey: dealModuleCountsKey,
    queryFn: fetchDealModuleCounts,
    enabled: enabled && !!currentOrganization?.id,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  const counts = {
    allDeals: data?.allDeals,
  };

  /**
   * Invalidate and refetch counts
   */
  const refetchCounts = () => {
    queryClient.invalidateQueries({ queryKey: dealModuleCountsKey });
  };

  return {
    counts,
    isLoading,
    error,
    refetchCounts,
  };
}

// Export query key for external use
export { dealModuleCountsKey };
